import { cloneElement, ReactElement, ReactNode, SyntheticEvent } from 'react';

import {
  Button,
  Dialog,
  DialogProps,
  SxProps,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

type FeedbackDialogProps = {
  icon: ReactElement<{ size?: number }>;
  title: ReactNode;
  body?: ReactNode;
  actions?: ReactNode;
  destroy: (event?: SyntheticEvent) => void;
  size?: number;
  contentSx?: SxProps;
  bodySx?: SxProps;
  testId?: string;
  DialogProps?: Omit<DialogProps, 'open'>;
};

/**
 * @deprecated Old dialog system — this component renders a floating `HexIcon` that overflows
 * the dialog border. UX has not yet decided how to redesign this pattern in the new system,
 * so dialogs that use `FeedbackDialog` (directly or indirectly) are blocked from migration.
 * Do not introduce new usages. The `/refactor-dialog <path>` skill will refuse to migrate
 * dialogs that depend on this and print the `contains floating hex icon` blocker.
 */
export const FeedbackDialog = ({
  size = 96,
  testId,
  DialogProps,
  ...props
}: FeedbackDialogProps) => {
  const iconEl = cloneElement(props.icon, { size });

  return (
    <Dialog
      {...DialogProps}
      open
      fullWidth
      maxWidth="sm"
      onClose={props.destroy}
      PaperProps={{ sx: { overflow: 'visible' } }}
      testId={testId}
    >
      <Stack
        direction="row"
        justifyContent="center"
        sx={{
          position: 'absolute',
          top: -1 * (size / 2),
          left: 0,
          width: '100%',
        }}
      >
        {iconEl}
      </Stack>
      <Stack py={56} pb={24} px={24} alignItems="center" sx={props.contentSx}>
        <Typography variant="h4" textAlign="center">
          {props.title}
        </Typography>
        <Typography
          variant="P12R"
          color={tokens.theme.fg.structural.secondary}
          mt={8}
          textAlign="center"
          sx={props.bodySx}
          component="div"
        >
          {props.body}
        </Typography>
      </Stack>
      {props.actions || (
        <Stack pb={24} alignItems="center" justifyContent="center">
          <Button onClick={props.destroy} variant="secondary">
            Close
          </Button>
        </Stack>
      )}
    </Dialog>
  );
};
